/**
 * Fallback profile used when the Firestore user document cannot be read (the
 * profile listener errored, or the doc came back missing and the repair fetch
 * failed too).
 *
 * The Firebase Auth user only carries identity fields — it knows nothing about
 * subscriptions, loved capsules, roles, or preferences. Writing a profile built
 * from it alone used to replace the stored profile wholesale, which silently
 * stripped `subscriptionData` from real Insiders and left them looking like free
 * users everywhere the app gates on it. So: keep everything already known about
 * this account, and only fill the gaps auth can actually speak to.
 *
 * A stored profile belonging to a different uid is discarded outright — never
 * merge one account's entitlements into another's session.
 */
export function buildAuthFallbackProfile(authUser, storedProfile = null) {
  const uid = String(authUser?.uid || "").trim();
  if (!uid) return null;

  const sameAccount = String(storedProfile?.uid || "").trim() === uid;
  const preserved = sameAccount ? { ...storedProfile } : {};

  const next = { ...preserved, uid };
  const displayName = String(authUser?.displayName || "").trim();
  const email = String(authUser?.email || "").trim();
  const profileImage = String(authUser?.photoURL || authUser?.profileImage || "").trim();

  if (displayName && !String(next.displayName || "").trim()) {
    next.displayName = displayName;
  }
  if (email && !String(next.email || "").trim()) {
    next.email = email;
  }
  if (email && !String(next.userName || "").trim()) {
    next.userName = email;
  }
  if (profileImage && !String(next.profileImage || "").trim()) {
    next.profileImage = profileImage;
  }

  return next;
}
